import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Stack, Tab } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router";
import type { Category } from "../../../../api/types/accessTypes";
import type { Investor } from "../../../../api/types/fundStructureTypes";
import type { ObjectClassDefinition, ObjectPermissions } from "../../../../api/types/objectTypes";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import ObjectTitle from "../../../common/ObjectTitle";
import FullHeightFlexPage from "../../../common/layout/FullHeightFlexPage";
import StretchedScrollableContent from "../../../common/layout/StretchedScrollableContent";
import { getDataCatalogueEntityType } from "../../../entityFields/entityTypesHelper";
import EntityCardFiles from "../../../entityFields/files/EntityCardFiles";
import { LockedFundraisingPopover } from "../common/LockedPortalPagePopovers";
import InvestorCashFlowsTab from "./cash-flows/InvestorCashFlowsTab";
import InvestorCommunicationMatrixTab from "./communication-matrix/InvestorCommunicationMatrixTab";
import InvestorDetailsDocumentsTab from "./documents/InvestorDetailsDocumentsTab";
import InvestorFundraisingDocumentsTab from "./fundraising-documents/InvestorFundraisingDocumentsTab";
import InvestorDetailsInvestmentsTab from "./investments/InvestorDetailsInvestmentsTab";
import InvestorMainInfoTab from "./main-info/InvestorMainInfoTab";

const tabPanelSx = { height: "100%", m: 0, px: 3, pt: 2.5, pb: 0 };

interface Props {
  investor: Investor;
  categories: Category[];
  objectDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
  onInvestorDetailsChange: (details: Partial<Investor>) => void;
}

const InvestorDetailsPage = ({
  investor,
  categories,
  objectDefinition,
  objectPermissions,
  onInvestorDetailsChange,
}: Props) => {
  const { tab } = useParams();
  const { hasAnyPermission, isPortalFeatureEnabled, investorPortalApp } = useClientContext();

  const [selectedTab, setSelectedTab] = useState(tab ?? "main");

  const hasPermissionsToViewFiles = hasAnyPermission(objectPermissions.filesReadPermissions);
  const hasPermissionsToManageFiles = hasAnyPermission(objectPermissions.filesWritePermissions);
  const hasPermissionsToViewFundraisings = hasAnyPermission(["ViewFundraisings", "ManageFundraisings"]);

  const onTabChange = (newTab: string) => {
    setSelectedTab(newTab);
    window.history.replaceState({}, document.title, window.location.pathname.replace(/\/[^/]+$/, `/${newTab}`));
  };

  return (
    <>
      <TabContext value={selectedTab}>
        <FullHeightFlexPage>
          <Box>
            <GeneralPageHeader
              TitleComponent={
                <ObjectTitle
                  title={investor.title}
                  typeName={`${objectDefinition.objectName} • Entity Type: ${investor.investorEntityType}`}
                />
              }
              hideBorderLine
              showDefaultBackButtonTo=".."
            />
            <TabList onChange={(_, tab) => onTabChange(tab)}>
              <Tab sx={{ ml: 3 }} label="Main Info" value="main" />
              <Tab label="Investments" value="investments" />
              <Tab label="Cash Flows" value="cashFlows" />
              <Tab label="Investor Documents" value="documents" />
              {hasPermissionsToViewFundraisings && (
                <Tab
                  disabled={!investorPortalApp}
                  component={investorPortalApp ? "button" : "div"}
                  label={
                    investorPortalApp ? (
                      "Fundraising Documents"
                    ) : (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ alignItems: "center", pointerEvents: "auto" }}
                      >
                        Fundraising Documents
                        <LockedFundraisingPopover />
                      </Stack>
                    )
                  }
                  value="fundraising-documents"
                />
              )}
              {hasPermissionsToViewFiles && isPortalFeatureEnabled("EntityFilesEnabled") && (
                <Tab label="Files" value="files" />
              )}
              <Tab label="Communication Matrix" value="communication-matrix" />
            </TabList>
          </Box>
          <StretchedScrollableContent>
            <TabPanel value="main" sx={{ ...tabPanelSx, backgroundColor: "background.grey" }}>
              <InvestorMainInfoTab
                investor={investor}
                objectDefinition={objectDefinition}
                objectPermissions={objectPermissions}
                onInvestorDetailsChange={onInvestorDetailsChange}
              />
            </TabPanel>
            <TabPanel value="investments" sx={tabPanelSx}>
              <InvestorDetailsInvestmentsTab investorId={investor.id} />
            </TabPanel>
            <TabPanel value="cashFlows" sx={tabPanelSx}>
              <InvestorCashFlowsTab investor={investor} />
            </TabPanel>
            <TabPanel value="documents" sx={tabPanelSx}>
              <InvestorDetailsDocumentsTab
                investorId={investor.id}
                investorName={investor.title}
                categories={categories}
                investorFunds={investor.funds}
              />
            </TabPanel>
            {hasPermissionsToViewFundraisings && investorPortalApp && (
              <TabPanel value="fundraising-documents" sx={tabPanelSx}>
                <InvestorFundraisingDocumentsTab
                  investorId={investor.id}
                  categories={categories}
                  investorFunds={investor.funds}
                />
              </TabPanel>
            )}
            {hasPermissionsToViewFiles && isPortalFeatureEnabled("EntityFilesEnabled") && (
              <TabPanel value="files" sx={{ height: "100%", m: 0, px: 0, py: 0 }}>
                <EntityCardFiles
                  entityType={getDataCatalogueEntityType(objectDefinition.objectType)}
                  entityId={investor.id}
                  entityName={investor.title}
                  hasPermissionsToManage={hasPermissionsToManageFiles}
                />
              </TabPanel>
            )}
            <TabPanel value="communication-matrix" sx={tabPanelSx}>
              <InvestorCommunicationMatrixTab investor={investor} categories={categories} />
            </TabPanel>
          </StretchedScrollableContent>
        </FullHeightFlexPage>
      </TabContext>
    </>
  );
};

export default InvestorDetailsPage;
